import React, { ComponentPropsWithoutRef, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, LinkProps, useLocation, useNavigate } from 'react-router-dom';

import logo from '@/assets/logo.png';
import selfServiceLogo from '@/assets/navbar/self-service_logo.png';
import Button from '@/components/ui/Button';
import InputSearch from '@/components/ui/InputSearch';
import { UserDropdown, useUser } from '@/features/auth';
import { useLocale } from '@/hooks/use-locale';
import { useMenuItems } from '@/hooks/use-menu-items';
import { useHeaderStore } from '@/store';
import { cn } from '@/utils/cn';

import LocaleSwitcher, { LocaleSwitcherRef } from './LocaleSwitcher';
import MobileHeader from './MobileHeader';

export interface MenuItem {
  key: string;
  url: string;
  icon?: React.ReactNode;
  isVisible?: boolean;
}

const AnimatedLink = ({ children, className, isActive = false, ...rest }: LinkProps & { isActive?: boolean }) => {
  return (
    <Link
      className={cn(
        'text-gray-7',
        'after:content-[""] whitespace-nowrap w-full after:block after:mx-auto after:h-[2px] after:w-0 after:bg-transparent after:transition-[width] after:duration-500 after:ease-in-out',
        {
          'border-b-brand-green border-b-2 text-gray-10': isActive,
          'hover:text-black hover:after:w-full hover:after:bg-brand-green': !isActive,
        },
        className,
      )}
      {...rest}
    >
      {children}
    </Link>
  );
};

export interface HeaderRefs {
  userElementRef: React.RefObject<HTMLDivElement>;
  localeElementRef: React.RefObject<LocaleSwitcherRef>;
  mobileMenuElementRef: React.RefObject<HTMLDivElement>;
  searchElementRef: React.RefObject<HTMLDivElement>;
}

interface HeaderProps extends ComponentPropsWithoutRef<'header'> {
  hasHeaderShadow?: boolean;
}

const Header = forwardRef<HeaderRefs, HeaderProps>(({ hasHeaderShadow, className, ...rest }, ref) => {
  const { t } = useTranslation();
  const { currentLocale, handleLocaleChange } = useLocale();

  const navigate = useNavigate();

  const { pathname } = useLocation();
  const { data: user } = useUser();
  const { menuItems } = useMenuItems();

  const {
    isMobileMenuVisible,
    setIsMobileMenuVisible,
    isUserDropdownVisible,
    setIsUserDropdownVisible,
    isLocaleDropdownVisible,
    setIsLocaleDropdownVisible,
    isSearchInputVisible,
    setIsSearchInputVisible,
  } = useHeaderStore();

  const [searchQuery, setSearchQuery] = useState('');

  const userElementRef = useRef<HTMLDivElement>(null);
  const localeElementRef = useRef<LocaleSwitcherRef>(null);
  const mobileMenuElementRef = useRef<HTMLDivElement>(null);
  const searchElementRef = useRef<HTMLDivElement>(null);

  const handleUserButtonClick = () => {
    setIsUserDropdownVisible(!isUserDropdownVisible);
    setIsLocaleDropdownVisible(false);
    setIsSearchInputVisible(false);
  };

  const handleLocaleButtonClick = () => {
    setIsLocaleDropdownVisible(!isLocaleDropdownVisible);
    setIsUserDropdownVisible(false);
    setIsMobileMenuVisible(false);
    setIsSearchInputVisible(false);
  };

  const handleMobileMenuButtonClick = () => {
    setIsMobileMenuVisible(!isMobileMenuVisible);
    setIsUserDropdownVisible(false);
    setIsLocaleDropdownVisible(false);
    setIsSearchInputVisible(false);
  };

  const onLocaleChange = (locale: string) => {
    setIsLocaleDropdownVisible(false);
    handleLocaleChange(locale);
  };

  const handleMobileMenuItemSelect = (item: MenuItem) => {
    setIsMobileMenuVisible(false);
    navigate(item.url);
  };

  const handleAccountSelect = () => {
    setIsUserDropdownVisible(false);
    setIsMobileMenuVisible(false);
  };

  useImperativeHandle(ref, () => ({
    userElementRef,
    localeElementRef,
    mobileMenuElementRef,
    searchElementRef,
  }));

  return (
    <header
      className={cn(
        'z-10 flex items-center bg-white px-4 md:px-5 lg:px-6 md:rounded-bl-2xl md:rounded-br-2xl h-[69px] md:h-[88px] lg:h-[116px]',
        className,
        hasHeaderShadow ? 'shadow-md' : 'shadow-none',
      )}
      {...rest}
    >
      <div className="flex w-full gap-x-2">
        <div className="flex items-center md:gap-x-12">
          <Link to="/" className="flex-shrink-0">
            {/* small logo */}
            <img src={logo} className="block lg:hidden w-10 h-[36.92px] object-contain" alt="logo" />

            {/* big logo */}
            <img src={selfServiceLogo} className="lg:block hidden w-[280px] h-[53px] object-contain" alt="logo" />
          </Link>

          {user && (
            <div className="items-center hidden md:flex gap-x-7">
              {menuItems
                .filter((menuItem) => menuItem.isVisible)
                .map((item) => (
                  <AnimatedLink isActive={pathname === item.url} key={item.key} to={item.url}>
                    {t(item.key, {
                      ns: 'header',
                    })}
                  </AnimatedLink>
                ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end flex-1 gap-x-2">
          <InputSearch
            ref={searchElementRef}
            isVisible={isSearchInputVisible && !isMobileMenuVisible}
            onInputChanged={(e) => {
              setSearchQuery(e.target.value);
            }}
            onCancelButtonClick={() => {
              setIsSearchInputVisible(false);
            }}
            onSearchIconClick={() => {
              setIsSearchInputVisible(true);
            }}
          />

          <Button
            className="md:flex flex-shrink-0 hidden px-1 w-[6.875rem]"
            href={`${import.meta.env.VITE_EKKT_HOMEPAGE_URL}?lang=${currentLocale}`}
            variant="primaryAlt"
          >
            <span className="overflow-hidden text-base text-ellipsis text-nowrap">{t('mainPage', { ns: 'header' })}</span>
          </Button>
          <LocaleSwitcher
            isDropdownVisible={isLocaleDropdownVisible}
            ref={localeElementRef}
            onLocaleButtonClick={handleLocaleButtonClick}
            onLocaleChange={onLocaleChange}
          />
          {user && (
            <UserDropdown
              className="hidden md:flex"
              userListClassName="max-h-[70vh] overflow-y-auto"
              isDropdownVisible={isUserDropdownVisible}
              ref={userElementRef}
              onUserButtonClick={handleUserButtonClick}
              onAccountSelect={handleAccountSelect}
            />
          )}
          <MobileHeader
            ref={mobileMenuElementRef}
            className="flex md:hidden"
            onHambugerButtonClick={handleMobileMenuButtonClick}
            onItemSelect={handleMobileMenuItemSelect}
            onAccountSelect={handleAccountSelect}
            isMobileMenuVisible={isMobileMenuVisible}
          />
        </div>
      </div>
    </header>
  );
});

export default Header;
